import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dalo8cvnn2hk28ekssig";

const CATEGORY_OPTIONS = [
  "事業に関するご相談",
  "協業・パートナーのご提案",
  "採用・求人について",
  "取材・メディアについて",
  "その他",
];

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("company_alt") || "").trim();
    if (honeypot) {
      setFormError("");
      setStatus("success");
      formEl.reset();
      return;
    }
    formData.delete("company_alt");

    setStatus("sending");
    setFormError("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setFormError("");
        setStatus("success");
        formEl.reset();
      } else {
        setFormError(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "送信に失敗しました。時間をおいて再度お試しください。",
        );
        setStatus("error");
      }
    } catch {
      setFormError("通信エラーが発生しました。時間をおいて再度お試しください。");
      setStatus("error");
    }
  };

  const inputClass =
    "w-full border-b border-background-300 bg-transparent py-3 font-body text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-foreground-950";
  const labelClass =
    "mb-2 block font-body text-[12px] tracking-[0.12em] text-foreground-700";

  if (status === "success") {
    return (
      <div className="flex flex-col items-center border border-background-300/70 bg-background-100 px-8 py-20 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full border border-accent-500 text-accent-600">
          <i className="ri-check-line text-3xl" />
        </span>
        <h3 className="mt-8 font-heading text-xl tracking-[0.06em] text-foreground-950">
          送信が完了しました
        </h3>
        <p className="mt-4 max-w-md font-body text-sm leading-[2] text-foreground-600">
          お問い合わせいただきありがとうございます。
          内容を確認のうえ、担当者より折り返しご連絡いたします。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-10 whitespace-nowrap border border-foreground-950 px-8 py-3 font-body text-[12px] tracking-[0.2em] text-foreground-950 transition-colors hover:bg-foreground-950 hover:text-background-50"
        >
          フォームに戻る
        </button>
      </div>
    );
  }

  return (
    <form
      data-readdy-form="true"
      id="revra-contact-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-8"
    >
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
        <div>
          <label className={labelClass} htmlFor="company">
            会社名
          </label>
          <input
            id="company"
            name="company"
            type="text"
            autoComplete="organization"
            placeholder="株式会社〇〇"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="name">
            お名前 <span className="text-accent-600">*</span>
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            autoComplete="name"
            placeholder="山田 太郎"
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
        <div>
          <label className={labelClass} htmlFor="email">
            メールアドレス <span className="text-accent-600">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="example@revra.jp"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="phone">
            電話番号
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            placeholder="03-0000-0000"
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label className={labelClass} htmlFor="category">
          お問い合わせ種別
        </label>
        <select id="category" name="category" defaultValue="" className={inputClass}>
          <option value="" disabled>
            選択してください
          </option>
          {CATEGORY_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className={labelClass} htmlFor="message">
          お問い合わせ内容 <span className="text-accent-600">*</span>
        </label>
        <textarea
          id="message"
          name="message"
          required
          rows={6}
          maxLength={500}
          placeholder="お問い合わせ内容をご記入ください（500文字以内）"
          className={`${inputClass} resize-none`}
        />
      </div>

      <input
        type="text"
        name="company_alt"
        className="form-aux-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="flex flex-col items-start gap-6">
        {status === "error" && formError ? (
          <p className="w-full border-l-2 border-accent-600 bg-accent-50 px-4 py-3 font-body text-sm text-accent-900">
            {formError}
          </p>
        ) : null}
        <button
          type="submit"
          disabled={status === "sending"}
          className="inline-flex items-center gap-3 whitespace-nowrap bg-foreground-950 px-10 py-4 font-body text-[12px] tracking-[0.24em] text-background-50 transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === "sending" ? "送信中..." : "送信する"}
          <i className="ri-arrow-right-line" />
        </button>
      </div>
    </form>
  );
}